import React, { FormEvent, ReactNode, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isSupabaseConfigured, supabase } from '../../lib/supabaseClient';
import { PrivacyPolicyContent } from '../../pages/ChloromanceTracker/PrivacyPolicy';
import { PLANT_LIMIT_NOTE } from '../../utils/Chloromance/constants';
import './ChloromanceAuthGate.css';

type ChloromanceAuthGateProps = {
  children: (user: User, openPrivacy: () => void, sessionBar: ReactNode) => ReactNode;
  showSessionBar?: boolean;
  renderSessionBarInChildren?: boolean;
  sessionBarPlacement?: 'top' | 'bottom';
};

type AuthMode = 'signin' | 'signup';

const MOBILE_AUTH_REDIRECT_URL = 'chloromance://auth/callback';

function getWebAuthRedirectUrl() {
  const configuredRedirectUrl =
    process.env.REACT_APP_SUPABASE_AUTH_REDIRECT_URL || process.env.VITE_SUPABASE_AUTH_REDIRECT_URL;

  if (configuredRedirectUrl) {
    return configuredRedirectUrl;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  const redirectPath = process.env.REACT_APP_BUILD_TARGET === 'chloromance' ? '/' : '/projects/chloromance-tracker';
  return new URL(redirectPath, window.location.origin).toString();
}

function getAuthRedirectUrl() {
  return Capacitor.isNativePlatform() ? MOBILE_AUTH_REDIRECT_URL : getWebAuthRedirectUrl();
}

async function handleAuthRedirect(url: string) {
  const parsedUrl = new URL(url);
  const hashParams = new URLSearchParams(parsedUrl.hash.replace(/^#/, ''));
  const accessToken = hashParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token');
  const code = parsedUrl.searchParams.get('code');

  if (accessToken && refreshToken) {
    await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    return;
  }

  if (code) {
    await supabase.auth.exchangeCodeForSession(code);
  }
}

function ChloromanceAuthGate({
  children,
  showSessionBar = true,
  renderSessionBarInChildren = false,
  sessionBarPlacement = 'top',
}: ChloromanceAuthGateProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [mode, setMode] = useState<AuthMode>('signin');
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });

    let removeAppUrlListener: (() => void) | undefined;

    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        if (url.startsWith(MOBILE_AUTH_REDIRECT_URL)) {
          handleAuthRedirect(url).catch((error) => setMessage(error.message));
        }
      }).then((listener) => {
        removeAppUrlListener = () => listener.remove();
      });

      CapacitorApp.getLaunchUrl().then((launchUrl) => {
        if (launchUrl?.url.startsWith(MOBILE_AUTH_REDIRECT_URL)) {
          handleAuthRedirect(launchUrl.url).catch((error) => setMessage(error.message));
        }
      });
    } else if (typeof window !== 'undefined' && (window.location.search || window.location.hash)) {
      handleAuthRedirect(window.location.href)
        .then(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch((error) => setMessage(error.message));
    }

    return () => {
      data.subscription.unsubscribe();
      removeAppUrlListener?.();
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage('');

    const authRequest =
      mode === 'signup'
        ? supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: getAuthRedirectUrl(),
          },
        })
        : supabase.auth.signInWithPassword({ email, password });

    const submittedEmail = email;
    const { error } = await authRequest;

    if (error) {
      setMessage(error.message);
    } else {
      if (mode === 'signup') {
        setPendingConfirmationEmail(submittedEmail);
        setPassword('');
      } else {
        setMessage('Welcome back.');
      }
    }

    setSubmitting(false);
  };

  const resetSignupConfirmation = () => {
    setPendingConfirmationEmail('');
    setMessage('');
    setMode('signup');
  };

  const openAuthForm = (nextMode: AuthMode) => {
    setMode(nextMode);
    setIntroDismissed(true);
    setMessage('');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const privacyModal = privacyOpen ? (
    <div className="auth-privacy-backdrop" role="presentation" onMouseDown={() => setPrivacyOpen(false)}>
      <section
        className="auth-privacy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-privacy-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="auth-privacy-header">
          <div>
            <p className="auth-kicker">Chloromance</p>
            <h2 id="auth-privacy-title">Privacy Policy</h2>
          </div>
          <button type="button" onClick={() => setPrivacyOpen(false)} aria-label="Close privacy policy">
            ×
          </button>
        </div>
        <div className="auth-privacy-content">
          <PrivacyPolicyContent />
        </div>
      </section>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="auth-screen">
        <section className="auth-card">
          <p className="auth-kicker">Chloromance</p>
          <h1>Preparing your plant ledger...</h1>
        </section>
      </div>
    );
  }

  if (!isSupabaseConfigured) {
    return (
      <>
        <div className="auth-screen">
          <section className="auth-card">
            <p className="auth-kicker">Chloromance</p>
            <h1>Supabase is not configured.</h1>
            <p>Add `REACT_APP_SUPABASE_URL` and `REACT_APP_SUPABASE_ANON_KEY` to your env before building.</p>
            <button className="auth-privacy-link" type="button" onClick={() => setPrivacyOpen(true)}>
              Privacy Policy
            </button>
          </section>
        </div>
        {privacyModal}
      </>
    );
  }

  if (session?.user) {
    const sessionBar = showSessionBar ? (
      <div className={`auth-session-bar ${sessionBarPlacement === 'bottom' ? 'is-bottom' : ''}`}>
        <span>{session.user.email}</span>
        <div className="auth-session-actions">
          <button type="button" onClick={() => setPrivacyOpen(true)}>
            Privacy
          </button>
          <button type="button" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </div>
    ) : null;

    return (
      <>
        {!renderSessionBarInChildren && sessionBarPlacement !== 'bottom' && sessionBar}
        {children(session.user, () => setPrivacyOpen(true), renderSessionBarInChildren ? sessionBar : null)}
        {!renderSessionBarInChildren && sessionBarPlacement === 'bottom' && sessionBar}
        {privacyModal}
      </>
    );
  }

  if (pendingConfirmationEmail) {
    return (
      <>
        <div className="auth-screen">
          <section className="auth-card auth-confirmation-card" aria-labelledby="auth-confirmation-title">
            <p className="auth-kicker">Chloromance</p>
            <h1 id="auth-confirmation-title">Go to your email to confirm.</h1>
            <p>
              We sent a confirmation link to <strong>{pendingConfirmationEmail}</strong>. Open that link to finish
              creating your account, then come back here to sign in.
            </p>
            <div className="auth-confirmation-actions">
              <button
                type="button"
                onClick={() => {
                  setPendingConfirmationEmail('');
                  setMessage('');
                  setMode('signin');
                }}
              >
                Sign In
              </button>
              <button type="button" onClick={resetSignupConfirmation}>
                Try Again
              </button>
            </div>
            <button className="auth-privacy-link" type="button" onClick={() => setPrivacyOpen(true)}>
              Privacy Policy
            </button>
          </section>
        </div>
        {privacyModal}
      </>
    );
  }

  if (!introDismissed) {
    return (
      <>
        <div className="auth-intro-screen">
          <section className="auth-intro" aria-labelledby="auth-intro-title">
            <div className="auth-intro-copy">
              <p className="auth-kicker">Chloromance</p>
              <h1 id="auth-intro-title">Plant care that feels calm, clever, and a little bit magic.</h1>
              <p>
                Build a private shelf-by-shelf plant list, see who needs water first, and keep your little green
                household thriving without trying to remember every last watering day.
              </p>
              <div className="auth-intro-actions">
                <button type="button" onClick={() => openAuthForm('signup')}>
                  Create Free Account
                </button>
                <button type="button" onClick={() => openAuthForm('signin')}>
                  I Already Have One
                </button>
              </div>
              <button className="auth-privacy-link" type="button" onClick={() => setPrivacyOpen(true)}>
                Privacy Policy
              </button>
            </div>

            <div className="auth-intro-preview" aria-label="Chloromance plant care preview">
              <div className="auth-phone-preview">
                <div className="auth-preview-header">
                  <span>Today</span>
                  <strong>72%</strong>
                </div>
                <div className="auth-preview-meter" aria-hidden="true">
                  <span />
                </div>
                <div className="auth-preview-plant is-thirsty">
                  <span>Monstera</span>
                  <strong>Water soon</strong>
                </div>
                <div className="auth-preview-plant">
                  <span>Basil</span>
                  <strong>Happy</strong>
                </div>
                <div className="auth-preview-plant is-critical">
                  <span>Peace Lily</span>
                  <strong>Needs care</strong>
                </div>
              </div>
              <div className="auth-intro-notes">
                <span>{PLANT_LIMIT_NOTE}</span>
                <span>Weather-aware care</span>
                <span>Private backup</span>
              </div>
            </div>
          </section>
        </div>
        {privacyModal}
      </>
    );
  }

  return (
    <>
      <div className="auth-screen">
        <section className="auth-card" aria-labelledby="auth-title">
          <p className="auth-kicker">Chloromance</p>
          <h1 id="auth-title">{mode === 'signin' ? 'Welcome back.' : 'Start your plant ledger.'}</h1>
          <p>Sign in to keep your plant data private on shared devices.</p>

          <div className="auth-mode-tabs" role="tablist" aria-label="Authentication mode">
            <button type="button" className={mode === 'signin' ? 'is-active' : ''} onClick={() => setMode('signin')}>
              Sign In
            </button>
            <button type="button" className={mode === 'signup' ? 'is-active' : ''} onClick={() => setMode('signup')}>
              Sign Up
            </button>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            <label>
              Email
              <input
                autoComplete="email"
                inputMode="email"
                required
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label>
              Password
              <input
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                minLength={6}
                required
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Working...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          {message && <p className="auth-message">{message}</p>}

          <button className="auth-privacy-link" type="button" onClick={() => setPrivacyOpen(true)}>
            Privacy Policy
          </button>
        </section>
      </div>
      {privacyModal}
    </>
  );
}

export default ChloromanceAuthGate;
